import React from 'react';
import styles from './ui.module.css';
import Icon from './Icon';

interface FileChipProps {
  name: string;
  /** Omit to show the chip without a remove control. */
  onRemove?: () => void;
  removeDisabled?: boolean;
  title?: string;
}

/** A chosen file: icon, name (ellipsised) and an optional remove control. */
export const FileChip: React.FC<FileChipProps> = ({ name, onRemove, removeDisabled, title }) => (
  <span className={styles.fileChip} title={title ?? name}>
    <Icon name="file" size={16} />
    <span className={styles.fileChipName}>{name}</span>
    {onRemove && (
      <button type="button" onClick={onRemove} disabled={removeDisabled} aria-label={`Remove ${name}`}>
        <Icon name="trash" size={15} />
      </button>
    )}
  </span>
);

interface FileDropzoneProps {
  /** Small uppercase caption in the zone's corner. */
  title?: React.ReactNode;
  /** Muted suffix after the title, e.g. "(optional)". */
  optionalLabel?: React.ReactNode;
  /** Extensions for the native picker, e.g. ['.xlsx', '.csv']. */
  accept: string[];
  /**
   * Called with what the user picked. Validation (extension checks, toasts)
   * stays with the caller so each page keeps its own rules and messages.
   */
  onFiles: (files: File[]) => void;
  multiple?: boolean;
  /** Also call `onFiles([])` when the picker is cancelled — for a page whose
   *  selection clears on cancel, as a bare multi-file input does. */
  reportEmpty?: boolean;
  /** Accept picks from the file dialog only, not files dropped on the zone —
   *  a drop bypasses `accept`, so a page without its own extension check can
   *  turn it off. */
  disableDrop?: boolean;
  /** Single-file mode: the chosen file replaces the drop target with a chip. */
  file?: File | null;
  onRemove?: () => void;
  mainText?: React.ReactNode;
  subText?: React.ReactNode;
  inputId?: string;
  /** Greys the zone out and blocks picking. */
  disabled?: boolean;
  /** An upload is running: blocks picking and removing, without greying out. */
  busy?: boolean;
  /** Rendered under the drop target — e.g. a list of FileChips in multi-file mode. */
  children?: React.ReactNode;
  className?: string;
}

/**
 * The dashed click-or-drop target every upload screen uses. The native file
 * input covers the whole zone, so dropping a file onto it works without any
 * drag handlers of our own.
 */
const FileDropzone: React.FC<FileDropzoneProps> = ({
  title,
  optionalLabel,
  accept,
  onFiles,
  multiple = false,
  reportEmpty = false,
  disableDrop = false,
  file,
  onRemove,
  mainText = 'Click or drag & drop',
  subText,
  inputId,
  disabled = false,
  busy = false,
  children,
  className = '',
}) => {
  const hasFile = !!file;
  const blockDrop = disableDrop
    ? { onDragOver: (e: React.DragEvent) => e.preventDefault(), onDrop: (e: React.DragEvent) => e.preventDefault() }
    : {};
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const picked = Array.from(e.target.files ?? []);
    if (picked.length === 0 && !reportEmpty) return;
    onFiles(picked);
  };

  return (
    <div
      className={[
        styles.dropzone,
        hasFile ? styles.dropzoneHasFile : '',
        disabled ? styles.dropzoneDisabled : '',
        className,
      ].filter(Boolean).join(' ')}
    >
      {title && (
        <span className={styles.dropzoneTitle}>
          {title}
          {optionalLabel && <span className={styles.dropzoneOptional}> {optionalLabel}</span>}
        </span>
      )}
      {hasFile && file ? (
        // Remove stays enabled while the zone is disabled, so an already-chosen
        // file can always be cleared; only a running upload blocks it.
        <FileChip name={file.name} onRemove={onRemove} removeDisabled={busy} />
      ) : (
        <div className={styles.dropzoneTarget}>
          <input
            type="file"
            id={inputId}
            accept={accept.join(',')}
            multiple={multiple}
            onChange={handleChange}
            disabled={busy || disabled}
            {...blockDrop}
          />
          <span className={styles.dropzoneIcon}><Icon name="upload" size={20} /></span>
          <span className={styles.dropzoneMain}>{mainText}</span>
          {subText && <span className={styles.dropzoneSub}>{subText}</span>}
        </div>
      )}
      {children}
    </div>
  );
};

export default FileDropzone;
